/**
 * Утилиты работы со временем в МСК.
 *
 * Зачем: Ozon API возвращает даты в UTC (с суффиксом Z), но русские продавцы
 * и интерфейс самого Ozon используют МСК. Сервер frogstat может стоять где
 * угодно (Хельсинки в нашем случае), и если использовать локальное время
 * сервера или UTC напрямую — границы дней «вчера/сегодня» в наших отчётах
 * не будут совпадать с тем что юзер видит в кабинете Ozon.
 *
 * Решение: ВЕЗДЕ в коде refresh-пайплайна использовать МСК (UTC+3, без DST)
 * как единственный часовой пояс для расчёта границ дней. Так наши цифры
 * будут совпадать с цифрами в кабинете Ozon независимо от того где стоит
 * сервер и где находится пользователь.
 *
 * Москва не переходит на летнее время с 2014 года, поэтому offset фиксированный.
 */

/** Сдвиг МСК относительно UTC в миллисекундах. UTC+3, без DST. */
export const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

/**
 * Возвращает UTC-таймстамп начала текущего МСК-дня (00:00 МСК) для
 * данного момента времени.
 *
 * Пример: если сейчас 2024-01-15T22:00:00Z (UTC), то в МСК это уже
 * 2024-01-16T01:00:00 — то есть наступило 16 января. Функция вернёт
 * UTC-таймстамп для 2024-01-16T00:00:00+03:00 = 2024-01-15T21:00:00Z.
 */
export function mskDayStart(utcMs: number = Date.now()): number {
  const mskShifted = new Date(utcMs + MSK_OFFSET_MS);
  return Date.UTC(
    mskShifted.getUTCFullYear(),
    mskShifted.getUTCMonth(),
    mskShifted.getUTCDate(),
  ) - MSK_OFFSET_MS;
}

/**
 * Возвращает строку "YYYY-MM-DD" в МСК для данного UTC-таймстампа.
 * Используется для индексации дней в массивах daily_30d и аналогичных.
 *
 * НЕЛЬЗЯ использовать `new Date(utcMs).toISOString().slice(0, 10)` —
 * это даст UTC-дату, не МСК.
 */
export function mskDateString(utcMs: number = Date.now()): string {
  const mskShifted = new Date(utcMs + MSK_OFFSET_MS);
  const y = mskShifted.getUTCFullYear();
  const m = String(mskShifted.getUTCMonth() + 1).padStart(2, "0");
  const d = String(mskShifted.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Час суток в Москве (0..23) для указанного UTC-момента. */
export function mskHour(utcMs: number = Date.now()): number {
  return new Date(utcMs + MSK_OFFSET_MS).getUTCHours();
}

/**
 * Сдвигает календарную дату YYYY-MM-DD на указанное число дней.
 * Здесь намеренно используется UTC: строка уже представляет календарный день,
 * а не момент времени, поэтому часовой пояс больше не должен её сдвигать.
 */
export function shiftIsoDate(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime())) return date;
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

/**
 * Парсит дату от Ozon API (ISO-строка с Z или с offset) в UTC-миллисекунды.
 * Просто обёртка над `new Date(s).getTime()` для семантической ясности —
 * чтобы в коде было видно «здесь мы парсим дату от Ozon, она UTC».
 */
export function parseOzonDate(s: string | null | undefined): number {
  if (!s) return 0;
  return new Date(s).getTime();
}
